/**
 * Manages block-level and script-level variable overrides.
 * Overrides take precedence over global user variables.
 */

import { substituteVariables } from '../../shared/index.js';
import type { VariableStore } from './VariableStore.js';

export class OverrideManager {
  private _blockOverrides: Record<number, Record<string, string>> = {};
  private _scriptOverrides: Record<string, Record<string, string>> = {};

  constructor(private store: VariableStore) {}

  // ============ Block Overrides ============

  /**
   * Set a per-block variable override.
   * If value matches global, the override is removed.
   */
  setBlockOverride(blockIndex: number, varName: string, value: string): void {
    if (!this._blockOverrides[blockIndex]) {
      this._blockOverrides[blockIndex] = {};
    }

    const globalValue = this.store.getUserVariable(varName);

    if (value === globalValue) {
      delete this._blockOverrides[blockIndex][varName];
      if (Object.keys(this._blockOverrides[blockIndex]).length === 0) {
        delete this._blockOverrides[blockIndex];
      }
    } else {
      this._blockOverrides[blockIndex][varName] = value;
    }
  }

  /**
   * Check if a block has any local overrides.
   */
  hasBlockOverrides(blockIndex: number): boolean {
    return !!this._blockOverrides[blockIndex] &&
           Object.keys(this._blockOverrides[blockIndex]).length > 0;
  }

  /**
   * Get all overrides for a block.
   */
  getBlockOverrides(blockIndex: number): Record<string, string> {
    return this._blockOverrides[blockIndex] || {};
  }

  /**
   * Clear all overrides for a specific block.
   */
  clearBlockOverrides(blockIndex: number): void {
    delete this._blockOverrides[blockIndex];
  }

  /**
   * Clear all block overrides.
   */
  clearAllBlockOverrides(): void {
    this._blockOverrides = {};
  }

  /**
   * Get the effective value for a variable, considering block overrides.
   */
  getBlockValue(varName: string, blockIndex: number | null = null): string | undefined {
    if (blockIndex !== null && this._blockOverrides[blockIndex]?.[varName] !== undefined) {
      return this._blockOverrides[blockIndex][varName];
    }
    return this.store.getUserVariable(varName);
  }

  /**
   * Apply variable substitution with block-level overrides merged in.
   */
  applyBlockVariables(template: string, blockIndex: number | null = null): string {
    const mergedVars = { ...this.store.userVariables };
    if (blockIndex !== null && this._blockOverrides[blockIndex]) {
      Object.assign(mergedVars, this._blockOverrides[blockIndex]);
    }
    return substituteVariables(template, mergedVars).result;
  }

  // ============ Script Overrides ============

  /**
   * Set a per-script variable override.
   * If value matches global, the override is removed.
   */
  setScriptOverride(scriptName: string, varName: string, value: string): void {
    if (!this._scriptOverrides[scriptName]) {
      this._scriptOverrides[scriptName] = {};
    }

    const globalValue = this.store.getUserVariable(varName);

    if (value === globalValue) {
      delete this._scriptOverrides[scriptName][varName];
      if (Object.keys(this._scriptOverrides[scriptName]).length === 0) {
        delete this._scriptOverrides[scriptName];
      }
    } else {
      this._scriptOverrides[scriptName][varName] = value;
    }
  }

  /**
   * Check if a script has any local overrides.
   */
  hasScriptOverrides(scriptName: string): boolean {
    return !!this._scriptOverrides[scriptName] &&
           Object.keys(this._scriptOverrides[scriptName]).length > 0;
  }

  /**
   * Get all overrides for a script.
   */
  getScriptOverrides(scriptName: string): Record<string, string> {
    return this._scriptOverrides[scriptName] || {};
  }

  /**
   * Clear all overrides for a specific script.
   */
  clearScriptOverrides(scriptName: string): void {
    delete this._scriptOverrides[scriptName];
  }

  /**
   * Clear all script overrides.
   */
  clearAllScriptOverrides(): void {
    this._scriptOverrides = {};
  }

  /**
   * Get the effective value for a variable in a script context.
   */
  getScriptValue(varName: string, scriptName: string | null = null): string | undefined {
    if (scriptName !== null && this._scriptOverrides[scriptName]?.[varName] !== undefined) {
      return this._scriptOverrides[scriptName][varName];
    }
    return this.store.getUserVariable(varName);
  }

  /**
   * Apply variable substitution with script-level overrides merged in.
   */
  applyScriptVariables(template: string, scriptName: string | null = null): string {
    const mergedVars = { ...this.store.userVariables };
    if (scriptName && this._scriptOverrides[scriptName]) {
      Object.assign(mergedVars, this._scriptOverrides[scriptName]);
    }
    return substituteVariables(template, mergedVars).result;
  }

  /**
   * Reset all overrides.
   */
  reset(): void {
    this._blockOverrides = {};
    this._scriptOverrides = {};
  }
}
